import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import Switch from '@/design-system/atoms/Forms/Switch';
import { IconList } from '@/design-system/atoms/Icons/IconList';
import { IconMap } from '@/design-system/atoms/Icons/IconMap';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearchParams } from '@/hooks/useSearchParams';

import { useLocationSearchContext } from '../../contexts/LocationSearchContext';
import { useLocationUIContext } from '../../contexts/LocationUIContext';
import { useLocationViewState } from '../../hooks/useLocationViewState';
import styles from './LocationToolbar.module.scss';

/**
 * The Location Toolbar is a component that displays the total number of results, the search radius and allows the user to toggle between map and list views.
 */
const LocationToolbar = ({ cta }) => {
    const searchParams = useSearchParams();

    const breakpoints = useBreakpoint();

    const { openNowLabel, openNowAriaLabel, largeBreakpoint } = useLocationUIContext();

    const {
        coveo: {
            controller: { querySummary },
        },
        handleShowResultList,
        handleShowMaps,
        handleOpenNow,
    } = useLocationSearchContext();

    const viewState = useLocationViewState();

    const [totalResults, setTotalResults] = React.useState(null);
    const [distance, setDistance] = React.useState('');

    const openNow = searchParams.get('open-now') === 'true';

    React.useEffect(() => {
        return querySummary.subscribe(() => {
            const { total } = querySummary.state;

            setTotalResults(total);
        });
    }, [querySummary]);

    React.useEffect(() => {
        const searchParamDistance = searchParams.get('distance');

        if (!searchParamDistance) {
            setDistance('');
            return;
        }

        setDistance(searchParamDistance);
    }, [searchParams]);

    return (
        <section className={styles[`location-toolbar`]}>
            <div className="type-t1">
                {totalResults != null && (
                    <>
                        {totalResults} results
                        {distance && (
                            <>
                                {' '}
                                <span className="type-t1b">within {distance} miles</span>
                            </>
                        )}
                    </>
                )}
            </div>
            <div className={styles[`location-toolbar__toggles`]}>
                {breakpoints?.large && (
                    <>
                        <Button
                            label={
                                viewState === 'result-list'
                                    ? largeBreakpoint?.mapView?.label
                                    : largeBreakpoint?.listView?.label
                            }
                            ariaLabel={
                                viewState === 'result-list'
                                    ? largeBreakpoint?.mapView?.ariaLabel
                                    : largeBreakpoint?.listView?.ariaLabel
                            }
                            buttonStyle={Button.STYLE.TERTIARY}
                            iconComponent={viewState === 'result-list' ? IconMap : IconList}
                            onClick={() => {
                                viewState === 'result-list' && handleShowMaps();
                                viewState === 'maps' && handleShowResultList();
                            }}
                        />
                        <div className={styles[`location-toolbar__divider`]} />
                    </>
                )}
                <Switch
                    isSelected={openNow}
                    onChange={handleOpenNow}
                    label={openNowLabel}
                    aria-label={openNowAriaLabel}
                />
            </div>
        </section>
    );
};

LocationToolbar.propTypes = {
    /**
     * The search radius
     */
    radius: PropTypes.number,
};

export default LocationToolbar;
